import { Formik, Form } from "formik";
import { useState } from "react";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import { Button } from "@volca/ui/components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@volca/ui/components/ui/input-otp";
import { LogoIcon } from "@volca/ui/components/ui/logo";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { useBreakpoints } from "@volca/ui/hooks/use-breakpoints";
import { toast } from "@volca/ui/lib/toast";
import { cn } from "@volca/ui/lib/utils";
import { InitiateVerificationResponse, IPayout } from "@/payouts/payouts";
import { cleanupPayoutVerification, useVerifyCode } from "@/payouts/query";

const verifyCodeSchema = Yup.object().shape({
  verification_code: Yup.string().required("Code is required"),
});

interface VerifyCodeProps {
  payout: IPayout;
  verificationData: InitiateVerificationResponse;
  onVerified: (redirectUrl: string, creditEnabled: boolean) => void;
  onResendCode: () => void;
}

export const VerifyCode = ({
  payout,
  verificationData,
  onVerified,
  onResendCode,
}: VerifyCodeProps) => {
  const { payoutEndpoint } = useParams();
  const { isSmallMobile } = useBreakpoints();

  const [autoSubmitEnabled, setAutoSubmitEnabled] = useState(true);
  const { mutate } = useVerifyCode(payoutEndpoint);

  const handleVerify = async (values: { verification_code: string }) => {
    if (values.verification_code.length !== 6) return;

    setAutoSubmitEnabled(false);

    return mutate(values.verification_code, {
      onSuccess: (response) => {
        const { redirect_url } = response.data;
        onVerified(
          redirect_url,
          verificationData?.credit_payment_enabled ?? false,
        );
      },
      onError: (error) => {
        toast.error("Failed to verify code", {
          description:
            error instanceof Error ? error.message : "Please try again",
        });
        setAutoSubmitEnabled(true);
      },
    });
  };

  const handleResendCode = () => {
    cleanupPayoutVerification(payout.endpoint);
    onResendCode();
    toast.success("Code resent");
  };

  return (
    <Stack
      align="center"
      className="m-auto min-h-screen w-full max-w-md bg-surface-raised p-8"
      gap={8}
    >
      <LogoIcon />
      <Text variant="title" render={<h1 />} className="text-center">
        You received a referral payment from {verificationData?.business_name}
      </Text>
      <Formik
        initialValues={{ verification_code: "" }}
        validationSchema={verifyCodeSchema}
        validateOnMount
        onSubmit={handleVerify}
      >
        {({ values, isSubmitting, isValid, setFieldValue }) => (
          <Form className="w-full">
            <div className="my-4">
              <InputOTP
                maxLength={6}
                value={values.verification_code}
                onChange={(value) => {
                  setFieldValue("verification_code", value);
                  if (autoSubmitEnabled && value.length === 6) {
                    handleVerify({ verification_code: value });
                  }
                }}
                containerClassName={cn("justify-center", {
                  "gap-1": isSmallMobile,
                  "gap-3": !isSmallMobile,
                })}
              >
                {Array.from({ length: 6 }).map((_, index) => (
                  <InputOTPGroup key={index}>
                    <InputOTPSlot
                      index={index}
                      className={cn(
                        "border-input-line bg-input text-center text-sm shadow-xs transition-colors data-[active=true]:border-focus",
                        {
                          "h-10 w-10": isSmallMobile,
                          "h-12 w-12": !isSmallMobile,
                        },
                      )}
                    />
                  </InputOTPGroup>
                ))}
              </InputOTP>
            </div>
            <Stack
              align="start"
              gap={4}
              justify="center"
              className="w-full px-12"
            >
              <Button
                type="submit"
                disabled={!isValid || isSubmitting}
                className="w-full"
                loading={isSubmitting}
              >
                Verify
              </Button>
              <Button
                variant="ghost"
                onClick={handleResendCode}
                className="w-full"
              >
                Resend Code
              </Button>
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};
